import React, { useCallback, useLayoutEffect, useState } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import { rem, Spacer, Button, AppView, CardView, ButtonGroup, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import Text from 'design-system/Text';
import { HEIGHT } from 'design-system/Button';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { OnboardingFlow } from 'utils/types/navigation';
import { useMarginBottom } from 'hooks/useMarginBottom';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectTradingAccountFromId } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const strings = {
    title: 'Review your address',
    titleJISA: 'Review your child’s address',
    subTitle: 'We’ll use your current address for the transfer. If your ISA provider has an old address on file, it may cause delays so we suggest updating it first',
    subTitleJISA: 'We’ll use your child’s address for the transfer. If your child’s Junior ISA provider has an old address on file, it may cause delays so we suggest updating it first',
    changeAddress: 'Change address',
    changeChildAddress: 'This is not my child’s address',
    continue: 'Continue',
};
const ProductTransferReviewAddressScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const { address, accountId } = route.params;
    const jisaAccount = useAppSelector((state) => selectTradingAccountFromId(state, accountId));
    const accountType = accountId ? 'JISA' : 'ISA';
    const [isChildFromUK, setIsChildFromUK] = useState(false);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', 2 * HEIGHT + SCREEN_BOTTOM_PADDING + rem(16), paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const changeAddress = useCallback(() => {
        if (accountType === 'JISA') {
            const childDetails = jisaAccount && 'child' in jisaAccount ? jisaAccount.child : undefined;
            if (childDetails) {
                const { address: _, ...childPersonalDetails } = childDetails;
                navigation.navigate('AddressPostCodeScreen', {
                    accountId,
                    flow: OnboardingFlow.JISA,
                    childDetails: childPersonalDetails,
                });
            }
            return;
        }
        navigation.navigate('ChangeAddressProgressScreen', { flow: 'CHANGE' });
    }, [accountId, accountType, jisaAccount]);
    const goToSearchProvider = useCallback(() => {
        navigation.navigate('SelectTransferAccountType', { accountId });
    }, [accountId]);
    const handleConfirmPress = useCallback(() => {
        setIsChildFromUK((oldState) => !oldState);
    }, []);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={accountType === 'JISA' ? strings.titleJISA : strings.title} subTitle={accountType === 'JISA' ? strings.subTitleJISA : strings.subTitle}/>

            <CardView noMarginTop noMarginBottom withPadding>
              <FormattedAddress {...address} uk/>
            </CardView>

            {accountType === 'JISA' && (<TouchableBounceCard onDark onPress={handleConfirmPress}>
                <View style={styles.row}>
                  <RadioButton type="multiple" isChecked={isChildFromUK}/>
                  <Spacer w={16}/>
                  <View style={styles.flex}>
                    <Text TextThin-14>I confirm that my child is a UK resident and is not a US person.</Text>
                  </View>
                </View>
              </TouchableBounceCard>)}
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brandReversed title={accountType === 'JISA' ? strings.changeChildAddress : strings.changeAddress} onPress={changeAddress}/>
            <Button brand title={strings.continue} onPress={goToSearchProvider} disabled={accountType === 'JISA' && !isChildFromUK}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
    },
    flex: {
        flex: 1,
    },
});
export default ProductTransferReviewAddressScreen;
